import React, { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux";

import actions from "@/actions";
import ConfirmDialog from "@/components/ConfirmDialog";
import ConstructionSelectorContainer from "@/containers/ConstructionSelectorContainer";
import ContentsContainer from "@/containers/ContentsContainer";
import HeaderContainer from "@/containers/HeaderContainer";
import { getMenuByName, isLayoutMenu } from "@/lib/common";
import * as util from "@/lib/common";
import restoreState from "@/lib/restoreState";
import storageManager from "@/lib/storageManager";
import { Construction, Customer, Place } from "@/models/construction";
import { RootState } from "@/reducers/types";
import { SxLayoutApp } from "@/sx-layout/components/SxLayoutApp";
import { Menu } from "@/sx-layout/models";

export const AuthenticatedApp = () => {
  const [appKey, setAppKey] = useState(0);
  const dispatch = useDispatch();

  const { confirm, fetchingMenus, fetchingMasters, fetchingConstructions, fetchingRoles, menus } = useSelector(
    (state: RootState) => state.app
  );
  const { system } = useSelector((state: RootState) => state.session);

  const showConstructionSelector = () => dispatch(actions.app.showConstructionSelector());
  const showPasswordEditor = () => dispatch(actions.app.showPasswordEditor());

  /* 進捗管理システム・重機配置システム共通のマスタデータを取得する */
  useEffect(() => {
    dispatch(
      actions.construction.fetchItems(
        (data: { constructions: Construction[]; customers: Customer[]; places: Place[] }) => {
          const constructions = data.constructions;
          const constructionId = util.getConstructionId() || 0;
          const construction = constructions.find((c) => c.construction_id === Number(constructionId));

          if (!construction) {
            util.clearConstructionId();
            dispatch(actions.app.showConstructionSelector());
            return;
          }

          util.setConstructionId(construction.construction_id);
          dispatch(actions.construction.save(construction));
        }
      )
    );
  }, []);

  const construction = useSelector((state: RootState) => state.construction.construction);

  useEffect(() => {
    if (!construction?.construction_id) return;
    dispatch(actions.app.fetchRoles());
  }, [construction?.construction_id, dispatch]);

  const restorePage = useCallback(
    (props: { url: string; dispatch: Dispatch<AnyAction>; construction: Construction; menus: Menu[] }) => {
      // システム・メニューを復元する
      const { url, dispatch, construction, menus } = props;
      const urlState = restoreState(url); // URLを解析したオブジェクト

      // システム権限
      const hasProgressRole = construction.func_progress === 1;
      const hasLayoutRole = construction.func_layout === 1;

      // システム・メニュー
      const menuFromUrl = urlState?.menu; // URLで指定したメニュー
      const menuItem = menuFromUrl ? getMenuByName(menuFromUrl) : undefined; // 定義済みのメニュー
      const masterMenu = menuItem ? menus.find((m) => m.menu_id === menuItem.id) : undefined; // マスタデータで定義されたメニュー
      let system: string;
      let menu: string;
      const systemFromLocalStorage = storageManager.getUserItem("system");
      if (hasProgressRole && !hasLayoutRole) {
        // 進捗管理システム権限のみ
        system = "progress";
        menu = masterMenu?.active && !isLayoutMenu(menuItem.name) ? menuItem.name : "home";
      } else if (!hasProgressRole && hasLayoutRole) {
        // 重機配置システム権限のみ
        system = "layout";
        menu = masterMenu?.active && isLayoutMenu(menuItem.name) ? menuItem.name : "machine";
      } else if (masterMenu?.active || menuItem?.name === "home") {
        // URLで指定したメニューが有効
        system = isLayoutMenu(menuItem.name) ? "layout" : "progress";
        menu = menuItem.name;
      } else if (systemFromLocalStorage) {
        // 前回開いていたシステム
        system = systemFromLocalStorage;
        menu = masterMenu?.active ? menuItem.name : system === "layout" ? "machine" : "home";
      } else {
        // デフォルト：進捗管理メニューのホーム画面
        system = "progress";
        menu = "home";
      }

      history.replaceState(
        undefined,
        undefined,
        `/#/${construction.construction_id}/${menu}${urlState?.queryString ? `?${urlState.queryString}` : ""}`
      );

      // システム・メニュー選択
      dispatch(actions.app.switchSystem(system, menu));
    },
    []
  );

  /* アプリケーションを開いてデータがそろったときに工事・システム・メニューを復元する */
  const appInit = useRef(false);
  useEffect(() => {
    if (appInit.current || !dispatch || !construction || !menus?.length) return;
    restorePage({
      url: window.location.href,
      dispatch,
      construction,
      menus,
    });
    appInit.current = true;
    dispatch(actions.app.appInit());
  }, [dispatch, construction, menus]);

  /* URL直接編集したときの制御 */
  const stateRef = useRef<any>();
  stateRef.current = { dispatch, construction, menus };
  useEffect(() => {
    const handleHashChange = (e) => {
      const { dispatch, construction, menus } = stateRef.current;
      restorePage({
        url: e.newURL,
        dispatch,
        construction,
        menus,
      });
    };

    const handlePopState = (e) => {
      const { dispatch } = stateRef.current;
      dispatch(actions.app.popState(e));
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const prevConstructionIdRef = useRef(construction?.construction_id);
  useEffect(() => {
    if (!construction?.construction_id || prevConstructionIdRef.current === construction.construction_id) return;
    /* 進捗管理システム・重機配置システム共通の工事に紐づくマスタデータを取得する */
    dispatch(actions.app.fetchMenus());
    dispatch(actions.construction.fetchMasters());
    dispatch(
      actions.construction.fetchExtensions((response) => {
        const extension = response.extensions.filter((item) => item.extension_type === 12).shift();
        if (extension) {
          dispatch(actions.construction.fetchLinkageTasks());
        }
      })
    );
    /* システムの権限確認 */
    if (construction?.construction_id && prevConstructionIdRef.current !== construction.construction_id) {
      // 工事を読み込んだ or 変更したとき
      if (construction.func_progress === 1 && construction.func_layout === 0) {
        // 進捗システムのみ
        dispatch(actions.app.switchSystem("progress"));
      } else if (construction.func_progress === 0 && construction.func_layout === 1) {
        // 重機システムのみ
        dispatch(actions.app.switchSystem("layout"));
      }
    }
    prevConstructionIdRef.current = construction?.construction_id;
  }, [construction]);

  const handleSaveConstruction = () => {
    setAppKey(appKey + 1);
    dispatch(actions.app.changeMenu("home"));
  };

  const fetching = fetchingMenus || fetchingMasters || fetchingConstructions || fetchingRoles;

  const { visibleConstructionSelector } = useSelector((state: RootState) => state.app);
  return (
    <>
      {visibleConstructionSelector && <ConstructionSelectorContainer onSave={handleSaveConstruction} />}
      {/* 重機配置システム */}
      {system === "layout" && <SxLayoutApp />}
      {/* 進捗管理システム */}
      {system === "progress" && (
        <div key={appKey} className={`wrapper ${fetching ? "loading" : ""}`}>
          <HeaderContainer
            showConstructionSelector={showConstructionSelector}
            showPasswordEditor={showPasswordEditor}
          />
          <ContentsContainer onSaveConstruction={handleSaveConstruction} />
          {confirm !== null && <ConfirmDialog {...confirm} />}
        </div>
      )}
    </>
  );
};
